import { zodResolver } from '@hookform/resolvers/zod';
import {
  type DiscountCapResource,
  ParkSettingsController_read_v1,
  ParkSettingsController_replace_v1,
  type ParkSettingsResource,
} from '@repo/api-client';
import { mostOffBill } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { PRICE_MALFORMED, PRICE_UNREPRESENTABLE } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import {
  formatPaise,
  isRepresentablePaise,
  paiseFromRupees,
  RUPEES_SHAPE,
  rupeesFromPaise,
} from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { LoadFailed, Screen } from '../back-office/Screen';

/**
 * The park's cap on a manually entered bill discount (#352,
 * blossom-rest-api#552): a percentage of the bill and a flat amount, **the
 * lower wins**, an empty bound does not apply, both empty is no cap.
 *
 * **Managers read, owners write**, mirroring `GET` (both) and `PUT` (owner) on
 * `/v1/park-settings`. It bounds what every cashier in the park can give away,
 * so a manager raising their own ceiling is exactly what it must not allow. A
 * manager is shown values with no field; the API refuses their `PUT` regardless.
 *
 * The figure on a bill comes from `mostOffBill`, the till's own rule, so what
 * the owner reads here is what a till will allow.
 */

/**
 * Basis points are hundredths of a percent — the same two-decimal shift as paise
 * from rupees, so the same digit-moving conversion serves both and no decimal is
 * ever multiplied. `10` is 1000, `12.5` is 1250.
 */
const basisPointsFromPercent = paiseFromRupees;
const percentFromBasisPoints = (basisPoints: number) =>
  rupeesFromPaise(basisPoints).replace(/\.00$/, '');

/** A whole-rupee figure without the `.00`, as a sentence says it: "up to ₹25". */
const sayPaise = (paise: number) => formatPaise(paise).replace(/\.00$/, '');

const PERCENT_MALFORMED = 'A percentage is a number like 10 or 12.5, with no % sign.';
const PERCENT_TOO_HIGH = 'At most 100%.';

const capSchema = z.object({
  percent: z
    .string()
    .trim()
    .refine((value) => value === '' || RUPEES_SHAPE.test(value), PERCENT_MALFORMED)
    .refine(
      (value) => !RUPEES_SHAPE.test(value) || basisPointsFromPercent(value) <= 10_000,
      PERCENT_TOO_HIGH,
    ),
  rupees: z
    .string()
    .trim()
    .refine((value) => value === '' || RUPEES_SHAPE.test(value), PRICE_MALFORMED)
    .refine(
      (value) => !RUPEES_SHAPE.test(value) || isRepresentablePaise(paiseFromRupees(value)),
      PRICE_UNREPRESENTABLE,
    ),
});

type CapValues = z.infer<typeof capSchema>;

const valuesOf = ({ maxBasisPoints, maxPaise }: DiscountCapResource): CapValues => ({
  percent: maxBasisPoints === null ? '' : percentFromBasisPoints(maxBasisPoints),
  rupees: maxPaise === null ? '' : rupeesFromPaise(maxPaise),
});

/** The cap the values state, or `null` while either is not yet a valid figure. */
function capOf(values: CapValues): DiscountCapResource | null {
  const parsed = capSchema.safeParse(values);

  if (!parsed.success) return null;

  const { percent, rupees } = parsed.data;

  return {
    maxBasisPoints: percent === '' ? null : basisPointsFromPercent(percent),
    maxPaise: rupees === '' ? null : paiseFromRupees(rupees),
  };
}

/** Two bills, so the owner sees each bound take over. */
const EXAMPLE_BILLS = [25_000, 250_000];

/** What the cap allows on a bill, in words — or the warning when there is no cap. */
function CapMeaning({ cap }: { cap: DiscountCapResource | null }) {
  if (cap === null) return null;

  if (cap.maxBasisPoints === null && cap.maxPaise === null) {
    return (
      <Alert>
        <IconAlertTriangle />
        <AlertTitle>No cap</AlertTitle>
        <AlertDescription>Any cashier may take a whole bill off.</AlertDescription>
      </Alert>
    );
  }

  return (
    <p className="text-sm text-foreground numeric">
      {EXAMPLE_BILLS.map(
        (bill) => `On a ${sayPaise(bill)} bill, up to ${sayPaise(mostOffBill(cap, bill))}.`,
      ).join(' ')}
    </p>
  );
}

type Loaded =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly settings: ParkSettingsResource; readonly saved: boolean }
  | { readonly kind: 'failed'; readonly error: unknown };

export function DiscountCap() {
  const client = useApiClient();
  const { session } = useSession();
  const mayEdit = session?.user.role === 'OWNER';

  const [state, setState] = useState<Loaded>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(ParkSettingsController_read_v1, { signal: controller.signal })
      .then((settings) => {
        if (live) setState({ kind: 'loaded', settings, saved: false });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  return (
    <Screen title="Discount cap" lede="The largest discount a cashier may give">
      <div className="max-w-[36rem] space-y-5 py-3">
        {state.kind === 'loading' && <FormSkeleton />}

        {state.kind === 'failed' && (
          <LoadFailed
            error={state.error}
            onRetry={reload}
            title="The discount cap could not be loaded"
          />
        )}

        {state.kind === 'loaded' && (
          <>
            {(state.saved || state.settings.updatedAt !== null) && (
              <Alert role={state.saved ? 'status' : undefined}>
                <IconCircleCheck />
                <AlertTitle>{state.saved ? 'Saved.' : 'Set.'}</AlertTitle>
                {state.settings.updatedAt !== null && (
                  <AlertDescription>
                    Last set {formatInstant(state.settings.updatedAt)}.
                  </AlertDescription>
                )}
              </Alert>
            )}

            {mayEdit ? (
              // Keyed on the stored moment, so a save re-opens on what the server kept.
              <CapForm
                key={state.settings.updatedAt ?? 'unset'}
                cap={state.settings.discountCap}
                onSaved={(settings) => setState({ kind: 'loaded', settings, saved: true })}
              />
            ) : (
              <CapRecord cap={state.settings.discountCap} />
            )}
          </>
        )}
      </div>
    </Screen>
  );
}

/** A manager's view: both bounds, what they mean, and nothing to change them with. */
function CapRecord({ cap }: { cap: DiscountCapResource }) {
  const noLimit = <span className="text-muted-foreground">No limit</span>;

  return (
    <div className="space-y-5">
      <dl className="space-y-4 text-sm">
        <div className="space-y-1.5">
          <dt className={fieldLabel}>Percentage of the bill</dt>
          <dd className="text-foreground numeric">
            {cap.maxBasisPoints === null
              ? noLimit
              : `${percentFromBasisPoints(cap.maxBasisPoints)}%`}
          </dd>
        </div>
        <div className="space-y-1.5">
          <dt className={fieldLabel}>Flat amount</dt>
          <dd className="text-foreground numeric">
            {cap.maxPaise === null ? noLimit : formatPaise(cap.maxPaise)}
          </dd>
        </div>
      </dl>
      <CapMeaning cap={cap} />
    </div>
  );
}

function CapForm({
  cap,
  onSaved,
}: {
  cap: DiscountCapResource;
  onSaved: (settings: ParkSettingsResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(capSchema),
    mode: 'onTouched',
    defaultValues: valuesOf(cap),
  });

  const watched = useWatch({ control: form.control });
  const live = capOf({ percent: watched.percent ?? '', rupees: watched.rupees ?? '' });

  const [formError, setFormError] = useState<string | null>(null);

  // Per body, not per mount: the screen stays open and is saved more than once.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: CapValues) => {
    const discountCap = capOf(values);

    if (pending || discountCap === null) return;

    setFormError(null);

    const body = { discountCap };
    const sending = attemptFor(attempt.current, JSON.stringify(body));
    attempt.current = sending;

    try {
      const saved = await client.request(ParkSettingsController_replace_v1, {
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      attempt.current = null;
      onSaved(saved);
    } catch (error) {
      setFormError(messageForFailure(error));
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="percent"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={fieldLabel}>Percentage of the bill</FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11 numeric`}
                    placeholder="10"
                    inputMode="decimal"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rupees"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={fieldLabel}>Flat amount, ₹</FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11 numeric`}
                    placeholder="100.00"
                    inputMode="decimal"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <CapMeaning cap={live} />

        {/* Above the submit and it stays there — a toast vanishes. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <div className="pt-3">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

/** The form's shape while it is read: labels and fields, no spinner. */
function FormSkeleton() {
  return (
    <div aria-hidden className="space-y-5">
      {[40, 32].map((width, index) => (
        <div key={index} className="space-y-2">
          <div
            className="h-3 animate-pulse rounded-3xl bg-muted"
            style={{ width: `${String(width)}%` }}
          />
          <div className="h-11 animate-pulse rounded-3xl bg-muted" />
        </div>
      ))}
    </div>
  );
}
